import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PlayArrow as SeedIcon, Visibility as ViewIcon, PlaylistPlay as RunAllIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import useShowDemoUi from '../../hooks/useShowDemoUi';
import AdminSeedDataRbacWorkspace from './AdminSeedDataRbacWorkspace';

const DEFAULT_TABS = [
  { key: 'rbac', label: 'RBAC & access' },
  { key: 'access', label: 'Access & identity' },
  { key: 'training', label: 'Training' },
  { key: 'work_auth', label: 'Work Authorization' },
  { key: 'lookup', label: 'Lookup catalogs' },
  { key: 'okr', label: 'OKR' },
  { key: 'demo_test', label: 'Demo / Test Data' }
];

const CARD_PALETTE = ['#5c6bc0', '#7e57c2', '#26a69a', '#ef6c00', '#42a5f5', '#ab47bc'];

/** Order, number badge, and colors for SEED Data tabs (keyed by catalog tab key). */
const SEED_TAB_META = {
  rbac: { seq: 1, main: '#3949ab', light: '#e8eaf6', muted: '#9fa8da' },
  access: { seq: 2, main: '#6a1b9a', light: '#f3e5f5', muted: '#ce93d8' },
  training: { seq: 3, main: '#00897b', light: '#e0f2f1', muted: '#80cbc4' },
  work_auth: { seq: 4, main: '#e65100', light: '#fff3e0', muted: '#ffcc80' },
  lookup: { seq: 5, main: '#1565c0', light: '#e3f2fd', muted: '#90caf9' },
  okr: { seq: 6, main: '#8e24aa', light: '#f3e5f5', muted: '#ce93d8' },
  demo_test: { seq: 7, main: '#c2185b', light: '#fce4ec', muted: '#f48fb1' },
};

function seedTabMeta(key) {
  return (
    SEED_TAB_META[key] || {
      seq: 0,
      main: '#546e7a',
      light: '#eceff1',
      muted: '#b0bec5',
    }
  );
}

export default function AdminSeedData() {
  const showDemoDataInUi = useShowDemoUi();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [filter, setFilter] = useState('');
  const [overview, setOverview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBody, setPreviewBody] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyDomain, setBusyDomain] = useState(null);
  const [runAllBusy, setRunAllBusy] = useState(false);
  const [abcDemoPanel, setAbcDemoPanel] = useState(null);

  const refreshAbcDemoPanel = useCallback(async () => {
    try {
      const d = await api.get('/admin/seed-data/abc-demo/overview');
      setAbcDemoPanel(d.data?.data || null);
    } catch {
      setAbcDemoPanel(null);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/seed-data/catalog');
      setOverview(r.data?.data || null);
      await refreshAbcDemoPanel();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load seed catalog');
      setOverview(null);
      setAbcDemoPanel(null);
    } finally {
      setLoading(false);
    }
  }, [refreshAbcDemoPanel]);

  useEffect(() => {
    load();
  }, [load]);

  const availableTabs = useMemo(() => {
    const fromApi = Array.isArray(overview?.tabs) ? overview.tabs : [];
    const base = fromApi.length ? fromApi : DEFAULT_TABS;
    if (!showDemoDataInUi) return base.filter((t) => t.key !== 'demo_test');
    return base;
  }, [overview?.tabs, showDemoDataInUi]);

  const tabKey = availableTabs[tab]?.key || availableTabs[0]?.key || 'access';

  useEffect(() => {
    if (!showDemoDataInUi && tabKey === 'demo_test') setTab(0);
  }, [showDemoDataInUi, tabKey]);

  useEffect(() => {
    if (tab >= availableTabs.length) setTab(0);
  }, [tab, availableTabs.length]);

  useEffect(() => {
    if (tabKey !== 'demo_test') return undefined;
    refreshAbcDemoPanel();
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshAbcDemoPanel();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [tabKey, location.pathname, refreshAbcDemoPanel]);

  const filteredSeeds = useMemo(() => {
    let seeds = (overview?.seeds || []).filter((s) => s.tab === tabKey);
    if (tabKey === 'demo_test') {
      seeds = seeds.filter((s) => s.id !== 'abc_demo_test_users');
    }
    const q = filter.trim().toLowerCase();
    if (!q) return seeds;
    return seeds.filter(
      (s) =>
        String(s.title || '').toLowerCase().includes(q) ||
        String(s.description || '').toLowerCase().includes(q) ||
        String(s.id || '').toLowerCase().includes(q)
    );
  }, [overview, tabKey, filter]);

  const openPreview = async (seedId) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewBody(null);
    try {
      const r = await api.get(`/admin/seed-data/${encodeURIComponent(seedId)}/preview`);
      setPreviewBody(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Preview failed');
      setPreviewBody(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const seedAbcDemoDomain = async (domain, hostingAccountId) => {
    const busyKey = `${hostingAccountId || ''}|${domain}`;
    setBusyDomain(busyKey);
    try {
      const body = { domain };
      if (hostingAccountId) body.hosting_account_id = hostingAccountId;
      const r = await api.post('/admin/seed-data/abc-demo/seed-domain', body);
      toast.success(r.data?.message || `Demo users seeded for @${domain}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Domain seed failed');
    } finally {
      setBusyDomain(null);
    }
  };

  const runSeed = async (seedId, body = {}) => {
    setBusyId(seedId);
    try {
      const r = await api.post(`/admin/seed-data/${encodeURIComponent(seedId)}/run`, body);
      const msg = r.data?.message || 'Done';
      if (r.data?.data?.skipped) {
        toast.info(msg);
      } else {
        toast.success(msg);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Seed failed');
    } finally {
      setBusyId(null);
    }
  };

  const runAll = async () => {
    if (!window.confirm('Run all seeds in the recommended order? Existing rows are left in place (idempotent).')) return;
    setRunAllBusy(true);
    try {
      const r = await api.post('/admin/seed-data/run-all');
      const results = r.data?.data?.results || [];
      const failed = results.filter((x) => !x.ok);
      if (failed.length) {
        toast.warn(`${failed.length} step(s) reported an issue. Check messages.`);
      } else {
        toast.success(r.data?.message || 'All seeds finished.');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Run-all failed');
    } finally {
      setRunAllBusy(false);
    }
  };

  if (loading && !overview) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            SEED Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            New environment setup: run seeds in order, or use <strong>Run all</strong>. Each step is idempotent — if data
            is already present, you will see a message that nothing more needs to be entered or modified.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={runAllBusy ? <CircularProgress size={18} color="inherit" /> : <RunAllIcon />}
          disabled={runAllBusy}
          onClick={runAll}
        >
          Run all seeds
        </Button>
      </Stack>

      {overview?.allComplete ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          All registered seeds report complete for this database. You can still re-open previews or run individual seeds
          (they will no-op when already satisfied).
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Some seeds are incomplete — use the cards below or <strong>Run all seeds</strong> to fill gaps.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 1,
          minHeight: 52,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': { minHeight: 48, py: 1 },
        }}
      >
        {availableTabs.map((t, i) => {
          const meta = seedTabMeta(t.key);
          const selected = tab === i;
          return (
            <Tab
              key={t.key}
              label={
                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
                  <Box
                    component="span"
                    sx={{
                      minWidth: 26,
                      height: 26,
                      borderRadius: '8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 800,
                      color: selected ? '#fff' : meta.main,
                      bgcolor: selected ? meta.main : alpha(meta.main, 0.12),
                      border: `2px solid ${selected ? meta.main : alpha(meta.main, 0.35)}`,
                    }}
                  >
                    {meta.seq}
                  </Box>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontWeight: selected ? 800 : 600,
                      color: selected ? meta.main : 'text.secondary',
                      lineHeight: 1.2,
                      textAlign: 'left',
                    }}
                  >
                    {t.label}
                  </Typography>
                </Stack>
              }
              sx={{
                mr: 0.75,
                px: 1.5,
                textTransform: 'none',
                borderRadius: '10px 10px 0 0',
                color: selected ? meta.main : 'text.secondary',
                bgcolor: selected ? meta.light : alpha(meta.main, 0.06),
                border: '2px solid',
                borderColor: selected ? meta.main : alpha(meta.muted, 0.45),
                borderBottom: selected ? `4px solid ${meta.main}` : `2px solid ${alpha(meta.muted, 0.35)}`,
                opacity: selected ? 1 : 0.92,
                transition: 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
                '&:hover': {
                  bgcolor: selected ? meta.light : alpha(meta.main, 0.14),
                  borderColor: selected ? meta.main : meta.muted,
                  opacity: 1,
                },
                '&.Mui-selected': {
                  color: meta.main,
                },
              }}
            />
          );
        })}
      </Tabs>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Tab order:{' '}
        {availableTabs.map((t) => {
          const m = seedTabMeta(t.key);
          return (
            <Box component="span" key={t.key} sx={{ mr: 1.5, whiteSpace: 'nowrap' }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: m.main,
                  mr: 0.5,
                  verticalAlign: 'middle',
                }}
              />
              {m.seq}. {t.label}
            </Box>
          );
        })}
      </Typography>

      {tabKey === 'demo_test' ? (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Alert severity="warning">
            Optional QA / training data — not part of <strong>Run all seeds</strong>. Demo users are created per{' '}
            <strong>hosting_company</strong> account (sister concerns). Each block below is one hosting company; domain
            cards match Settings. <strong>Seed this domain</strong> sends users for that hostname on that hosting account.
            <strong> Seed all on this hosting</strong> loads every active domain for that company only.{' '}
            <strong>Seed all domains</strong> runs the same for <em>every</em> hosting company. Shared password is the
            same for all template users. <strong>Data clearance</strong> removes demo mailboxes for all hosting-linked
            domains plus legacy template addresses.
          </Alert>
          {abcDemoPanel?.mislinked_hint ? (
            <Alert severity="info" sx={{ py: 0.75 }}>
              {abcDemoPanel.mislinked_hint}
            </Alert>
          ) : null}
          {abcDemoPanel?.non_hosting_domains_hint ? (
            <Alert severity="warning" sx={{ py: 0.75 }}>
              {abcDemoPanel.non_hosting_domains_hint}
            </Alert>
          ) : null}
          {abcDemoPanel?.off_hosting_domains_hint ? (
            <Alert severity="warning" sx={{ py: 0.75 }}>
              {abcDemoPanel.off_hosting_domains_hint}
            </Alert>
          ) : null}
          {abcDemoPanel?.shared_password ? (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Shared demo password:{' '}
              <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.25, borderRadius: 1 }}>
                {abcDemoPanel.shared_password}
              </Box>
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => openPreview('abc_demo_test_users')}
            >
              View seed data
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={busyId === 'abc_demo_test_users' ? <CircularProgress size={16} color="inherit" /> : <SeedIcon />}
              disabled={busyId === 'abc_demo_test_users'}
              onClick={() => runSeed('abc_demo_test_users')}
            >
              Seed all domains (all hosting companies)
            </Button>
          </Stack>
          {Array.isArray(abcDemoPanel?.hosting_companies) && abcDemoPanel.hosting_companies.length > 0 ? (
            <Stack spacing={3}>
              {abcDemoPanel.hosting_companies.map((hc) => (
                <Box key={hc.hosting_account_id}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {hc.company_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hc.account_code} · hosting_company · {hc.domains?.length ?? 0} domain row(s)
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      startIcon={
                        busyId === 'abc_demo_test_users' ? <CircularProgress size={16} color="inherit" /> : <SeedIcon />
                      }
                      disabled={busyId === 'abc_demo_test_users'}
                      onClick={() => runSeed('abc_demo_test_users', { hosting_account_id: hc.hosting_account_id })}
                    >
                      Seed all on this hosting
                    </Button>
                  </Stack>
                  <Grid container spacing={2}>
                    {(hc.domains || []).map((d) => {
                      const busyKey = `${d.hosting_account_id || ''}|${d.domain}`;
                      const busy = busyDomain === busyKey;
                      const canSeed = !d.seed_disabled_reason;
                      return (
                        <Grid item xs={12} md={6} key={d.company_domain_id || `${hc.hosting_account_id}-${d.domain}`}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              borderRadius: 2,
                              borderLeft: `5px solid ${d.is_primary ? '#6366f1' : '#94a3b8'}`,
                            }}
                          >
                            <CardContent>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Mailbox domain
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                    @{d.domain}
                                  </Typography>
                                  {d.linked_company ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      Row: {d.linked_company}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  <Chip size="small" label="Hosting" color="success" variant="outlined" />
                                  {d.is_primary ? (
                                    <Chip size="small" label="Primary" color="primary" />
                                  ) : (
                                    <Chip size="small" label="Secondary" variant="outlined" />
                                  )}
                                  {d.is_active === false ? (
                                    <Chip size="small" label="Inactive" color="default" variant="outlined" />
                                  ) : null}
                                </Stack>
                              </Stack>
                              {d.seed_disabled_reason ? (
                                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }} variant="outlined">
                                  <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                                    {d.seed_disabled_reason}
                                  </Typography>
                                </Alert>
                              ) : null}
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Demo users fully linked: {d.demo_users_ok ?? 0} / {d.expected ?? 12}
                              </Typography>
                              {d.complete ? (
                                <Chip size="small" label="Complete" color="success" sx={{ mt: 1 }} variant="outlined" />
                              ) : (
                                <Chip size="small" label="Incomplete" color="warning" sx={{ mt: 1 }} variant="outlined" />
                              )}
                              <Divider sx={{ my: 1.5 }} />
                              {canSeed ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SeedIcon />}
                                  disabled={busy}
                                  onClick={() => seedAbcDemoDomain(d.domain, hc.hosting_account_id)}
                                >
                                  Seed this domain
                                </Button>
                              ) : (
                                <Tooltip title={d.seed_disabled_reason || 'Cannot seed this domain'}>
                                  <span>
                                    <Button size="small" variant="contained" startIcon={<SeedIcon />} disabled>
                                      Seed this domain
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                              {Array.isArray(d.sample_logins) && d.sample_logins.length > 0 ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                  Sample logins: {d.sample_logins.map((s) => s.email).join(' · ')}
                                </Typography>
                              ) : null}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Stack>
          ) : null}
          {Array.isArray(abcDemoPanel?.other_domains) && abcDemoPanel.other_domains.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Domains not on a hosting company account
              </Typography>
              <Grid container spacing={2}>
                {abcDemoPanel.other_domains.map((d) => {
                  const busyKey = `|${d.domain}`;
                  const busy = busyDomain === busyKey;
                  const canSeed = !d.seed_disabled_reason;
                  return (
                    <Grid item xs={12} md={6} key={d.company_domain_id || `other-${d.domain}`}>
                      <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, borderLeft: '5px solid #cbd5e1' }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Mailbox domain
                              </Typography>
                              <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                @{d.domain}
                              </Typography>
                              {d.linked_company ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  Row: {d.linked_company}
                                </Typography>
                              ) : null}
                            </Box>
                            <Chip size="small" label="Not hosting" color="warning" variant="outlined" />
                          </Stack>
                          {d.seed_disabled_reason ? (
                            <Alert severity="warning" sx={{ mt: 1, py: 0.5 }} variant="outlined">
                              <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                                {d.seed_disabled_reason}
                              </Typography>
                            </Alert>
                          ) : null}
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Demo users fully linked: {d.demo_users_ok ?? 0} / {d.expected ?? 12}
                          </Typography>
                          <Divider sx={{ my: 1.5 }} />
                          {canSeed ? (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SeedIcon />}
                              disabled={busy}
                              onClick={() => seedAbcDemoDomain(d.domain)}
                            >
                              Seed this domain
                            </Button>
                          ) : (
                            <Tooltip title={d.seed_disabled_reason || 'Cannot seed this domain'}>
                              <span>
                                <Button size="small" variant="contained" startIcon={<SeedIcon />} disabled>
                                  Seed this domain
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {Array.isArray(d.sample_logins) && d.sample_logins.length > 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                              Sample logins: {d.sample_logins.map((s) => s.email).join(' · ')}
                            </Typography>
                          ) : null}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : null}
          {!abcDemoPanel?.hosting_companies?.length && !abcDemoPanel?.other_domains?.length ? (
            <Alert severity="info">
              No company domains are configured yet. Under Accounts, create one or more <strong>hosting_company</strong>{' '}
              accounts (sister concerns), then add domain rows under Settings → Company domains for each hosting company
              you want to use for demo mailboxes.
            </Alert>
          ) : null}
        </Stack>
      ) : null}

      {tabKey === 'rbac' ? <AdminSeedDataRbacWorkspace onRefreshCatalog={load} /> : null}

      {tabKey !== 'demo_test' && tabKey !== 'rbac' ? (
        <>
          <TextField
            size="small"
            fullWidth
            placeholder="Filter by title, description, or id…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2}>
            {filteredSeeds.map((s) => {
              const done = !!s.status?.complete;
              const globalNum = (overview?.seeds || []).findIndex((x) => x.id === s.id) + 1;
              const border = CARD_PALETTE[(globalNum - 1) % CARD_PALETTE.length];
              const busy = busyId === s.id;
              return (
                <Grid item xs={12} md={6} key={s.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      borderLeft: `5px solid ${border}`,
                      boxShadow: done ? 0 : 1
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Chip label={globalNum} size="small" sx={{ fontWeight: 800, bgcolor: border, color: '#fff' }} />
                        <Chip
                          size="small"
                          label={done ? 'Loaded' : 'Incomplete'}
                          color={done ? 'success' : 'warning'}
                          variant={done ? 'filled' : 'outlined'}
                        />
                      </Stack>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                        {s.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                        {s.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {s.description}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                        {s.status?.summary}
                      </Typography>
                      {s.status?.metrics && Object.keys(s.status.metrics).length > 0 ? (
                        <Box
                          component="pre"
                          sx={{
                            mt: 0.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            fontSize: 11,
                            overflow: 'auto'
                          }}
                        >
                          {JSON.stringify(s.status.metrics, null, 2)}
                        </Box>
                      ) : null}
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ViewIcon />}
                          onClick={() => openPreview(s.id)}
                        >
                          View seed data
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SeedIcon />}
                          disabled={busy}
                          onClick={() => runSeed(s.id)}
                        >
                          Seed
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        Source: {Array.isArray(s.sourceFiles) ? s.sourceFiles.join(', ') : '—'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {filteredSeeds.length === 0 && !loading ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              No seeds match this tab or filter.
            </Typography>
          ) : null}
        </>
      ) : null}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Seed preview</DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <CircularProgress />
          ) : previewBody ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Status
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {previewBody.status?.summary}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sample / definition (not a full DB export)
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  overflow: 'auto',
                  fontSize: 12,
                  maxHeight: 420
                }}
              >
                {JSON.stringify(previewBody.preview, null, 2)}
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">No preview.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
